import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { motion } from 'framer-motion';
import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

const shimmerKeyframes = keyframes`
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
`;

const ShimmerDiv = styled(motion.div)`
  background: linear-gradient(
    90deg,
    rgba(75, 75, 75, 0.2) 25%,
    rgba(125, 125, 125, 0.3) 50%,
    rgba(75, 75, 75, 0.2) 75%
  );
  background-size: 200% 100%;
  animation: ${shimmerKeyframes} 1.6s infinite linear;
`;

export function SkeletonCard() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 80);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Box sx={{ mb: 4, perspective: 800 }}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95, rotateY: 3 }}
        animate={{
          opacity: isLoaded ? 1 : 0,
          scale: isLoaded ? 1 : 0.95,
          rotateY: isLoaded ? 0 : 3,
        }}
        transition={{ duration: 0.6 }}
        className="w-full min-h-[360px] rounded-lg shadow-lg border border-gray-700 bg-gray-800 overflow-hidden relative"
      >
        <ShimmerDiv
          initial={{ opacity: 0 }}
          animate={{ opacity: isLoaded ? 1 : 0 }}
          transition={{ duration: 0.6 }}
          className="w-full h-48 bg-gray-700 rounded-t-lg"
        />
        <Box sx={{ p: 3 }}>
          <ShimmerDiv
            initial={{ width: 0 }}
            animate={{ width: isLoaded ? '80%' : 0 }}
            transition={{ delay: 0.2, duration: 0.4 }}
            className="h-6 bg-gray-600 mb-2 rounded"
          />
          <ShimmerDiv
            initial={{ width: 0 }}
            animate={{ width: isLoaded ? '100%' : 0 }}
            transition={{ delay: 0.4, duration: 0.4 }}
            className="h-4 bg-gray-600 mb-2 rounded"
          />
          <ShimmerDiv
            initial={{ width: 0 }}
            animate={{ width: isLoaded ? '70%' : 0 }}
            transition={{ delay: 0.6, duration: 0.4 }}
            className="h-4 bg-gray-600 mb-2 rounded"
          />
          <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {[...Array(3)].map((_, index) => (
              <ShimmerDiv
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: isLoaded ? 1 : 0, y: isLoaded ? 0 : 10 }}
                transition={{ delay: 0.8 + index * 0.2, duration: 0.4 }}
                className="h-6 bg-gray-600 w-16 rounded-full"
              />
            ))}
          </Box>
          <Box
            sx={{
              mt: 4,
              display: 'flex',
              alignItems: 'center',
              color: 'grey.500',
              fontSize: '12px',
            }}
          >
            <ShimmerDiv
              initial={{ width: 0 }}
              animate={{ width: isLoaded ? '48px' : 0 }}
              transition={{ delay: 1.4, duration: 0.4 }}
              className="h-5 bg-gray-600 rounded"
            />
            <Box
              sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 2 }}
            >
              {[...Array(2)].map((_, i) => (
                <ShimmerDiv
                  key={i}
                  initial={{ scale: 0 }}
                  animate={{ scale: isLoaded ? 1 : 0 }}
                  transition={{ delay: 1.6 + i * 0.2, duration: 0.3 }}
                  className="h-5 w-5 bg-gray-600 rounded-full"
                />
              ))}
            </Box>
          </Box>
        </Box>
      </motion.div>
    </Box>
  );
}
